/**
 * Orchestration tab: displays process triggers for the product (read-only in Catalogue).
 * Rule Set V2 defines which processes are triggered when the product is sold/engaged.
 * Actions supported: Create ProcessCase, Reserve resource, Call integration/webhook.
 * Edit rules in Studio.
 */

import { useEffect, useState } from "react";
import { ExternalLink, Loader2, Info } from "lucide-react";
import { useCurrentRuleSet, useRuleSetRules } from "../../../api/opsV2/hooks";
import { getProduct, type Product } from "../../../api/catalog";

type ProductOrchestrationTabProps = {
  productId: string;
};

/** Types de produits pour lesquels l'orchestration est recommandée (services, licences, forfaits). */
const ORCHESTRATION_RECOMMENDED_TYPES = ["service_area", "service_fixed", "custom_service", "subscription", "license_credit", "package"];

export function ProductOrchestrationTab({ productId }: ProductOrchestrationTabProps) {
  const [product, setProduct] = useState<Product | null>(null);
  const { data: currentRuleSet, loading: loadingRuleSet, error: errorRuleSet, refetch } = useCurrentRuleSet(productId);
  const { data: rules, loading: loadingRules } = useRuleSetRules(currentRuleSet?.id ?? null);

  useEffect(() => {
    getProduct(productId).then(setProduct).catch(() => setProduct(null));
  }, [productId]);

  if (loadingRuleSet) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-muted">
        <Loader2 className="h-4 w-4 animate-spin" />
        Chargement…
      </div>
    );
  }

  if (errorRuleSet) {
    return (
      <div className="p-6">
        <p className="text-sm text-destructive">{errorRuleSet.message}</p>
        <button type="button" onClick={() => refetch()} className="mt-2 text-sm text-primary hover:underline">
          Réessayer
        </button>
      </div>
    );
  }

  const isOrchestrationRecommended = product && ORCHESTRATION_RECOMMENDED_TYPES.includes(product.product_type ?? "");
  const showRecommendationAlert = !currentRuleSet && isOrchestrationRecommended;

  return (
    <div className="p-4 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-text">Processus déclenchés</h3>
        <p className="text-xs text-muted mt-0.5">
          Définit ce qui se déclenche opérationnellement quand ce produit est vendu ou engagé : création de dossier (ProcessCase), réservation de ressource ou appel d&apos;intégration. Les règles sont modifiées dans le Studio.
        </p>
      </div>

      {showRecommendationAlert && (
        <div className="rounded-lg border border-border bg-primary/5 p-3 flex gap-2 text-sm text-muted">
          <Info className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
          <p>
            Pour les services, licences et forfaits, il est recommandé de définir une orchestration (Rule Set) pour déclencher les processus à la vente.
          </p>
        </div>
      )}

      {!currentRuleSet ? (
        <div className="rounded-lg border border-border bg-muted/20 p-4 text-sm text-muted">
          <p>Aucun processus déclenché pour ce produit.</p>
          <p className="mt-1 text-xs">Créez et publiez un Rule Set dans le Studio pour définir les actions (création de dossier, réservation, intégration).</p>
          <a
            href={`#/studio/rule-sets?product_id=${productId}`}
            className="mt-3 inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Ouvrir dans Studio
          </a>
        </div>
      ) : (
        <>
          {/* Rule Set courant (processus déclenchés) */}
          <div className="rounded-lg border border-border bg-surface p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <span className="font-medium text-text">{currentRuleSet.name ?? `Règles v${currentRuleSet.version_no}`}</span>
                <span className="ml-2 text-xs text-muted">v{currentRuleSet.version_no}</span>
                {currentRuleSet.status === "published" && (
                  <span className="ml-2 rounded bg-success/20 px-1.5 py-0.5 text-xs text-success">Publié</span>
                )}
              </div>
              <a
                href={`#/studio/rule-sets?product_id=${productId}`}
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Ouvrir dans Studio
              </a>
            </div>
            {currentRuleSet.published_at && (
              <p className="mt-1 text-xs text-muted">
                Publié le {new Date(currentRuleSet.published_at).toLocaleDateString("fr-CA", { dateStyle: "medium" })}
              </p>
            )}
          </div>

          {/* Règles d'orchestration */}
          <div>
            <h4 className="text-sm font-medium text-text mb-2">Règles d&apos;orchestration</h4>
            {loadingRules ? (
              <div className="flex items-center gap-2 text-sm text-muted py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Chargement…
              </div>
            ) : !rules || rules.length === 0 ? (
              <p className="text-sm text-muted">Aucune règle dans ce Rule Set.</p>
            ) : (
              <div className="rounded-md border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left p-3 font-medium text-text">Clé</th>
                      <th className="text-left p-3 font-medium text-text">Template</th>
                      <th className="text-left p-3 font-medium text-text w-28">Déclenchement</th>
                      <th className="text-left p-3 font-medium text-text w-24">Obligatoire</th>
                      <th className="text-left p-3 font-medium text-text w-24">Origine</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map((r) => (
                      <tr key={r.id} className="border-b border-border/50 hover:bg-muted/20">
                        <td className="p-3 font-medium text-text">{r.rule_key}</td>
                        <td className="p-3 text-muted">{r.process_template_id.slice(0, 8)}…</td>
                        <td className="p-3 text-muted">{r.start_policy}</td>
                        <td className="p-3 text-muted">{r.required ? "Oui" : "Optionnel"}</td>
                        <td className="p-3 text-muted">{r.origin_type}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
